import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SoapPayload, VisitFullResponse, VoidResponse } from '@yui-receipt/shared'
import { api, ApiClientError } from '@/lib/api'
import { toastError, toastSuccess } from '@/store/toast'
import { formatDateTime } from '@/lib/format'

/**
 * Read-only view of a finalized SOAP note. Displayed after `/visits/:id/review`
 * has been attested. Mirrors the three-section SOAP layout without edit
 * affordances — the note is locked by the BE and attempts to edit return 409.
 *
 * Therapists can void a final (with a reason) up until retention cut-off;
 * the void action is the only writable action on this page.
 */
export default function VisitFinalPage() {
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery<VisitFullResponse>({
    queryKey: ['visit', id],
    queryFn: () => api(`/visits/${id}`),
    enabled: !!id,
  })

  const voidMut = useMutation({
    mutationFn: (reason: string) =>
      api<VoidResponse>(`/visits/${id}/final/void`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: () => {
      toastSuccess('ยกเลิกฉบับที่เซ็นแล้ว')
      qc.invalidateQueries({ queryKey: ['visit', id] })
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiClientError ? err.message : 'ยกเลิกไม่สำเร็จ'
      toastError(msg)
    },
  })

  function handleVoid() {
    const reason = window.prompt('เหตุผลการยกเลิก:')
    if (!reason?.trim()) return
    voidMut.mutate(reason.trim())
  }

  if (isLoading) {
    return <p className="max-w-3xl mx-auto p-6 text-slate-500">กำลังโหลด…</p>
  }
  if (error || !data) {
    return (
      <p className="max-w-3xl mx-auto p-6 text-rose-600">
        โหลดไม่สำเร็จ —{' '}
        <Link to="/visits" className="underline">
          กลับ
        </Link>
      </p>
    )
  }

  const final = data.final
  const payload: SoapPayload | undefined = final?.json ?? data.draft?.json
  if (!final || !payload) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <p className="text-slate-600">ยังไม่มีฉบับที่เซ็นยืนยัน —</p>
        <Link to={`/visits/${id}/review`} className="underline text-brand-700">
          ไปหน้าตรวจทาน
        </Link>
      </div>
    )
  }

  const isVoided = !!final.voidedAt

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">
            โน้ตที่เซ็นยืนยัน — {data.visit.patientNameSnapshot}
          </h1>
          <p className="text-xs text-slate-500">
            HN {data.visit.patientHnSnapshot} • {data.visit.visitDate} • เซ็นเมื่อ{' '}
            {formatDateTime(final.attestedAt)}
          </p>
          <p className="text-xs text-slate-500 font-mono">
            License: {final.therapistLicense} • Hash: {final.noteHash.slice(0, 12)}…
          </p>
        </div>
        <Link to="/visits" className="text-sm text-slate-500 hover:text-brand-600">
          ← กลับ
        </Link>
      </header>

      {isVoided && (
        <div
          role="status"
          className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900"
        >
          <strong>ถูกยกเลิกแล้ว</strong> — เมื่อ {formatDateTime(final.voidedAt ?? '')}
          {final.voidReason ? ` · เหตุผล: ${final.voidReason}` : ''}
        </div>
      )}

      <ReadonlySection title="S — ผู้ป่วยเล่า">
        <Row label="อาการสำคัญ" value={payload.subjective.chiefComplaint.value} />
        <Row
          label="VAS (0-10)"
          value={payload.subjective.painVas.value?.toString() ?? null}
        />
        <Row label="ตำแหน่งปวด" value={payload.subjective.painLocation.value} />
        <Row label="ลักษณะอาการปวด" value={payload.subjective.painQuality.value} />
        <Row label="ระยะเวลา/เริ่มเป็น" value={payload.subjective.onset.value} />
        <Lines label="ปัจจัยกระตุ้น" items={payload.subjective.aggravatingFactors} />
        <Lines label="ปัจจัยบรรเทา" items={payload.subjective.relievingFactors} />
        <Row
          label="การใช้ชีวิตก่อนบาดเจ็บ"
          value={payload.subjective.priorLevelOfFunction.value}
        />
        <Lines label="เป้าหมายผู้ป่วย" items={payload.subjective.patientGoals} />
        <Row label="โรคประจำตัว" value={payload.subjective.pmh.value} />
        <Lines label="ยา" items={payload.subjective.medications} />
        <Lines label="แพ้ยา" items={payload.subjective.allergies} />
      </ReadonlySection>

      <ReadonlySection title="O — ตรวจร่างกาย">
        <Row label="การสังเกต" value={payload.objective.observation.value} />
        <div className="text-sm text-slate-600 italic">
          สัญญาณชีพ, ROM, MMT, Special tests — ดูรายละเอียดในหน้าตรวจทาน
        </div>
      </ReadonlySection>

      <ReadonlySection title="A — สรุป">
        <Row label="PT diagnosis" value={payload.assessment.ptDiagnosis.value} />
        <Row
          label="Clinical reasoning"
          value={payload.assessment.clinicalReasoning.value}
        />
        <Row label="Progress note" value={payload.assessment.progressNote.value} />
        <Row
          label="เหตุผลการรักษา"
          value={payload.assessment.skilledRationale.value}
        />
      </ReadonlySection>

      <ReadonlySection title="P — แผน">
        <Row label="ความถี่" value={payload.plan.frequency.value} />
        <Row
          label="ระยะเวลา"
          value={
            payload.plan.durationWeeks.value != null
              ? `${payload.plan.durationWeeks.value} สัปดาห์`
              : null
          }
        />
        <Lines label="HEP" items={payload.plan.hep} />
        <Lines label="คำแนะนำผู้ป่วย" items={payload.plan.patientEducation} />
        <Row label="นัดครั้งถัดไป" value={payload.plan.nextVisit.value} />
      </ReadonlySection>

      {!isVoided && (
        <div className="pt-2">
          <button
            type="button"
            className="btn-ghost border border-rose-200 text-rose-600"
            onClick={handleVoid}
            disabled={voidMut.isPending}
          >
            ❌ ยกเลิกฉบับนี้
          </button>
        </div>
      )}
    </div>
  )
}

function ReadonlySection({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="card space-y-2">
      <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
        {title}
      </h2>
      {children}
    </section>
  )
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-3 text-sm">
      <div className="sm:w-48 text-slate-500 shrink-0">{label}</div>
      <div className="text-slate-800 whitespace-pre-wrap">
        {value ?? <span className="italic text-slate-400">—</span>}
      </div>
    </div>
  )
}

function Lines({ label, items }: { label: string; items: string[] }) {
  if (!items || items.length === 0) return <Row label={label} value={null} />
  return (
    <div className="flex flex-col sm:flex-row sm:gap-3 text-sm">
      <div className="sm:w-48 text-slate-500 shrink-0">{label}</div>
      <ul className="text-slate-800 list-disc pl-5">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  )
}
